import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import validator from "validator";

import Drawer from "components/drawerSlide/drawer";
import Input from "components/form/input/Input";
import Toggle from "components/form/toggle/Toggle";
import { Badge } from "components/badge/Badge";
import { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import { useAppDispatch } from "hooks/redux";
import { showSuccess } from "redux/actions/ui";

import "./NewCustomerDrawer.scss";

interface INewCustomerDrawerProps {
  open: boolean;
  onClose: () => void;
  searchValue?: string;
  onCreateCustomer: (customerInfo: ICustomerInfoState) => Promise<number | null>;
  onNavigateToProfile: (customerId: number) => void;
}

function emptyFormState(): ICustomerInfoState {
  return {
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
    emailAddressValid: { isDirty: false, isValid: false },
    sendWelcomeEmail: false,
    sendMarketingEmails: false,
  };
}

function setEmailValidState(email: string) {
  return email === "" ? { isDirty: false, isValid: false } : { isDirty: true, isValid: validator.isEmail(email) };
}

function applySearchPrefill(searchValue: string): ICustomerInfoState {
  const base = emptyFormState();

  if (searchValue.includes("@")) {
    return {
      ...base,
      emailAddress: searchValue,
      emailAddressValid: { isDirty: true, isValid: validator.isEmail(searchValue) },
    };
  }

  if (validator.isNumeric(searchValue)) {
    return { ...base, phoneNumber: searchValue };
  }

  if (searchValue.includes(" ")) {
    const fullName = searchValue.split(" ");
    const next = { ...base };
    if (validator.isAlpha(fullName[0])) {
      next.firstName = fullName[0];
    }
    if (validator.isAlpha(fullName[1])) {
      next.lastName = fullName[1];
    }
    return next;
  }

  if (validator.isAlpha(searchValue)) {
    return { ...base, firstName: searchValue };
  }

  return base;
}

function measureSiteHeaderBottom(): number {
  const header = document.querySelector(".ui-header");
  return header ? Math.ceil(header.getBoundingClientRect().bottom) : 90;
}

function isTextInputTarget(target: EventTarget | null): target is HTMLInputElement {
  if (!(target instanceof HTMLInputElement)) {
    return false;
  }

  const type = target.type;
  return type === "text" || type === "email" || type === "tel" || type === "";
}

export default function NewCustomerDrawer(props: INewCustomerDrawerProps) {
  const { open, onClose, searchValue, onCreateCustomer, onNavigateToProfile } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [state, setState] = useState<ICustomerInfoState>(emptyFormState);
  const [customersAddedCount, setCustomersAddedCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawerTop, setDrawerTop] = useState(90);

  useEffect(() => {
    if (!open) {
      setState(emptyFormState());
      setCustomersAddedCount(0);
      setIsSubmitting(false);
      return;
    }

    setState(searchValue ? applySearchPrefill(searchValue) : emptyFormState());
    setCustomersAddedCount(0);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const updateDrawerTop = () => {
      setDrawerTop(measureSiteHeaderBottom());
    };

    updateDrawerTop();
    window.addEventListener("resize", updateDrawerTop);

    return () => {
      window.removeEventListener("resize", updateDrawerTop);
    };
  }, [open]);

  const isSubmitDisabled =
    isSubmitting ||
    state.firstName === "" ||
    state.lastName === "" ||
    (!state.emailAddressValid?.isValid && state.phoneNumber === "") ||
    (state.emailAddressValid?.isDirty && !state.emailAddressValid?.isValid && state.phoneNumber?.length > 0);

  const emailHasError = state.emailAddressValid?.isDirty && !state.emailAddressValid?.isValid;

  const subtitle =
    customersAddedCount > 0
      ? customersAddedCount === 1
        ? t("1 customer added so far. Add the next one below.")
        : t("{{count}} customers added so far. Add the next one below.", { count: customersAddedCount })
      : t("Add new customer now. Complete their profile later.");

  function handleClose() {
    if (isSubmitting) {
      return;
    }

    onClose();
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({
      ...prevState,
      [id]: value,
      emailAddressValid: id === "emailAddress" ? setEmailValidState(value) : prevState.emailAddressValid,
    }));
  }

  function handleToggleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = event.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  function clearFormFields() {
    setState(emptyFormState());
  }

  async function submitCustomer(mode: "add" | "addAnother") {
    if (isSubmitDisabled) {
      return;
    }

    try {
      setIsSubmitting(true);
      const customerId = await onCreateCustomer({ ...state });

      if (customerId === null) {
        return;
      }

      dispatch(showSuccess(t("New customer added successfully")));

      if (mode === "add") {
        onNavigateToProfile(customerId);
        onClose();
        return;
      }

      setCustomersAddedCount(prev => prev + 1);
      clearFormFields();
    } finally {
      setIsSubmitting(false);
    }
  }

  const isSubmittingRef = useRef(isSubmitting);
  const isSubmitDisabledRef = useRef(isSubmitDisabled);
  const submitCustomerRef = useRef(submitCustomer);

  isSubmittingRef.current = isSubmitting;
  isSubmitDisabledRef.current = isSubmitDisabled;
  submitCustomerRef.current = submitCustomer;

  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        if (isSubmittingRef.current) {
          return;
        }

        onClose();
        return;
      }

      if (event.key !== "Enter" || isSubmitDisabledRef.current) {
        return;
      }

      if (!isTextInputTarget(event.target)) {
        return;
      }

      event.preventDefault();
      void submitCustomerRef.current("add");
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  return (
    <Drawer
      open={open}
      title={t("New customer")}
      subtitle={subtitle}
      className="new-customer-drawer"
      style={{ top: drawerTop, height: `calc(100vh - ${drawerTop}px)` }}
      onClose={handleClose}
      onOk={() => void submitCustomer("add")}
      okText={t("Add customer")}
      okDisabled={isSubmitDisabled}
      secondaryActions={[
        {
          label: t("Save & add another"),
          action: () => void submitCustomer("addAnother"),
          buttonType: "secondary",
          disabled: isSubmitDisabled,
        },
      ]}
      hideBackdrop
    >
      <Drawer.Section bordered>
        <div className="new-customer-drawer-fields">
          <Input
            label={t("First name*")}
            placeholder={t("Enter first name…")}
            id="firstName"
            name="firstName"
            autoComplete="given-name"
            value={state.firstName}
            onChange={handleInputChange}
            disabled={isSubmitting}
          />
          <Input
            label={t("Last name*")}
            placeholder={t("Enter last name…")}
            id="lastName"
            name="lastName"
            autoComplete="family-name"
            value={state.lastName}
            onChange={handleInputChange}
            disabled={isSubmitting}
          />
          <div>
            <div className="new-customer-drawer-contact-header">
              <p className="new-customer-drawer-contact-title">{t("Contact method*")}</p>
              <Badge type="warning" size="small" outline>
                {t("At least one required")}
              </Badge>
            </div>
            <div className="new-customer-drawer-contact-fields">
              <Input
                label={t("Email")}
                placeholder={t("Enter email address…")}
                id="emailAddress"
                name="email"
                type="email"
                autoComplete="email"
                value={state.emailAddress}
                onChange={handleInputChange}
                error={emailHasError}
                helpText={emailHasError ? t("Please enter a valid email address") : undefined}
                disabled={isSubmitting}
              />
              <Input
                label={t("Phone number")}
                placeholder={t("Enter phone number…")}
                id="phoneNumber"
                name="phone"
                type="tel"
                autoComplete="tel"
                value={state.phoneNumber}
                onChange={handleInputChange}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      </Drawer.Section>

      <Drawer.Section>
        <Toggle
          id="sendWelcomeEmail"
          size="small"
          labelRight={t("Send welcome email")}
          labelSupport={t("A welcome email will be sent to the customer confirming their new account.")}
          onChange={handleToggleChange}
          checked={state.sendWelcomeEmail}
          disabled={isSubmitting}
        />
        <Toggle
          id="sendMarketingEmails"
          size="small"
          labelRight={t("Send marketing emails")}
          labelSupport={t(
            "When opted in, this customer will receive promotional emails and newsletters to stay engaged with your offerings.",
          )}
          onChange={handleToggleChange}
          checked={state.sendMarketingEmails ?? false}
          disabled={isSubmitting}
        />
      </Drawer.Section>
    </Drawer>
  );
}
